import { buffers, eventChannel } from 'redux-saga';
import { all, take, put } from 'redux-saga/effects';
import CustormMqttClient from '@/fetch/mqtt/client';
import {
  ConnectionStatus,
  EventName,
  MqttTopic,
  TopicPath,
  MessageType,
  ActionTypeMap,
} from '@/utils/constant';
import { store } from './store';
import { watchHTTPRequest } from './fetchHandler';
import {
  addGlobalEventListener,
  removeGlobalEventListener,
  sendGlobalEvent,
} from '@/utils/emit';
import { SYNC_ANDRIOD_CONNECT_STATUS } from './actions/common';
import { Status } from './reducers/common';
import { isEmpty } from 'lodash';
import { driveDataDTOType } from '@/utils/protoTypes';

let wsChannel: any;

type WsEmitData = {
  type: ConnectionStatus;
  data?: any;
};

const WS_DECODE_TASK_ID = 'saga-ws-decode';

const safePerfEnter = (taskId: string) => {
  try {
    (window as any).__PERF_RECORDER__?.enter?.(taskId);
  } catch {}
};

const safePerfQuit = (taskId: string) => {
  try {
    (window as any).__PERF_RECORDER__?.quit?.(taskId);
  } catch {}
};

/**
 * 解析blob数据为arraybuffer
 *
 * @param {Blob} blob
 * @return {*}  {Promise<ArrayBuffer>}
 */
const readBlobAsArrayBuffer = (blob: Blob): Promise<ArrayBuffer> => {
  const anyBlob = blob as any;
  if (typeof anyBlob?.arrayBuffer === 'function') {
    return anyBlob.arrayBuffer();
  }

  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
      } else {
        reject(new Error('FileReader result is not an ArrayBuffer'));
      }
    };

    reader.onerror = () => {
      reject(reader.error || new Error('FileReader failed'));
    };

    reader.readAsArrayBuffer(blob);
  });
};

/**
 * 直接解析proto数据
 *
 * @param {ArrayBuffer} ab
 * @return {*}
 */
const decodeDriveDataFromArrayBuffer = (ab: ArrayBuffer) => {
  const buf = new Uint8Array(ab);
  return driveDataDTOType.decode(buf);
};

/**
 * WS onmessage消息处理器
 */
const createWsOnMessageHandler = (emit: (data: WsEmitData) => void) => {
  return (e: MessageEvent) => {
    safePerfEnter(WS_DECODE_TASK_ID);

    let shouldQuitInFinally = true;

    try {
      const data = e.data;

      if (data instanceof ArrayBuffer) {
        const res = decodeDriveDataFromArrayBuffer(data);
        emit({ type: ConnectionStatus.MESSAGE, data: res });
        return;
      }

      // 兼容：某些环境仍可能收到 Blob（或服务端未按预期发送二进制帧）
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        shouldQuitInFinally = false;
        readBlobAsArrayBuffer(data)
          .then((ab) => {
            const res = decodeDriveDataFromArrayBuffer(ab);
            emit({ type: ConnectionStatus.MESSAGE, data: res });
          })
          .catch((error) => {
            console.error('WS decode failed:', error);
          })
          .then(() => {
            safePerfQuit(WS_DECODE_TASK_ID);
          });
        return;
      }
    } catch (error) {
      console.error('WS decode failed:', error);
      return;
    } finally {
      if (shouldQuitInFinally) {
        safePerfQuit(WS_DECODE_TASK_ID);
      }
    }
  };
};

const createWsEventChannal = (wsUrl: string) => {
  return eventChannel<WsEmitData>((emit) => {
    let ws: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // 创建接受ws数据的回调
    const onWsMessage = createWsOnMessageHandler(emit);

    const connect = () => {
      ws = new WebSocket(wsUrl);
      // 直接以 ArrayBuffer 的形式接收二进制帧
      ws.binaryType = 'arraybuffer';

      ws.onopen = () => emit({ type: ConnectionStatus.CONNECTION_OK });
      ws.onmessage = onWsMessage;

      ws.onerror = (error) =>
        emit({ type: ConnectionStatus.CONNECTION_ERROR, data: error });

      ws.onclose = () => {
        emit({ type: ConnectionStatus.CONNECTION_LOST });
        // 在这里进行重连，但要确保能被清理
        timer = setTimeout(connect, 1000);
      };

      (window as any).wsClient = ws;
    };

    connect();

    return () => {
      console.log('Cleaning up WebSocket and Timers...');
      if (timer) clearTimeout(timer);
      if (ws) {
        // 彻底切断引用
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      }
    };
  }, buffers.expanding<WsEmitData>(32));
};

function* watchWsResponse(): IterableIterator<any> {
  if (!wsChannel) return;

  let isConnected = false;

  while (true) {
    const emitData: any = yield take(wsChannel);

    switch (emitData.type) {
      case ConnectionStatus.CONNECTION_OK:
        isConnected = true;
        console.log('WS 连接成功，开始处理业务');
        break;

      case ConnectionStatus.MESSAGE:
        {
          if (!isConnected) continue;
          const { messageType } = emitData.data || {};
          const actionType =
            ActionTypeMap.get(
              !isEmpty(messageType) ? messageType[0] : MessageType.UNKNOWN_TYPE
            ) || 'UNKNOWN_TYPE';
          yield put({ type: actionType, payload: emitData.data });
        }
        break;

      case ConnectionStatus.CONNECTION_LOST:
        isConnected = false;
        console.warn('WS 已断开');
        break;

      case ConnectionStatus.CONNECTION_ERROR:
        isConnected = false;
        console.warn('WS 连接异常:', emitData?.data);
        break;
    }
  }
}

// 创建基于mqtt的Channel
type MqttEmitData = {
  type: ConnectionStatus;
  topic?: string;
  data?: any;
};

const mqttChannel = eventChannel<MqttEmitData>((emitter: any) => {
  let innerMqttClient: any = null;

  const createCb = () => {
    const { login, newCommon } = store.getState();
    if (innerMqttClient) return;

    const clientId = `${login.userInfo?.userName}_monitor_drive_${Date.now()}`;
    innerMqttClient = new CustormMqttClient(process.env.JDX_APP_REMOTE_MQTT!, {
      clientId: clientId,
      username: login.userInfo?.userName || '',
      password: login.mqttToken,
      subscribes: [
        `${MqttTopic.ISSUE_INFO}${login.userInfo?.userName}`,
        `${MqttTopic.USER_INFO}${login.userInfo?.userName}`,
        `${MqttTopic.TEAM_INFO}${newCommon.cockpitTeamNumber}`,
        `${MqttTopic.COMMAND_RESULT}${login.userInfo?.userName}`,
      ],
      onConnect: (mqtt: any) => {
        store.dispatch({ type: 'newCommon/setIsMqttConnected', payload: true });
        mqtt.publish(
          `${MqttTopic.MQTT_WILL}${login.userInfo?.userName}`,
          JSON.stringify({
            header: {
              requestId: Date.now(),
              clientName: clientId,
              requestTime: Date.now(),
              needResponse: false,
            },
            data: { online: true },
          })
        );
      },
      onError: () => {
        store.dispatch({
          type: 'newCommon/setIsMqttConnected',
          payload: false,
        });
      },
      will: {
        topic: `${MqttTopic.MQTT_WILL}${login.userInfo?.userName}`,
        payload: JSON.stringify({
          header: {
            requestId: Date.now(),
            clientName: clientId,
            requestTime: Date.now(),
            needResponse: false,
          },
          data: { online: false },
        }),
        qos: 2,
        retain: false,
      },
      onMessage: (topic: string, data: any) => {
        return emitter({
          topic,
          type: ConnectionStatus.MESSAGE,
          data: data,
        });
      },
    });
    (window as any).mqttClient = innerMqttClient;
  };

  addGlobalEventListener(EventName.CREATE_MQTT_CLIENT, createCb);

  const closeCb = () => {
    if (innerMqttClient) {
      innerMqttClient.end();
      innerMqttClient = null;
      if ((window as any).mqttClient) {
        delete (window as any).mqttClient;
      }
      store.dispatch({ type: 'newCommon/setIsMqttConnected', payload: false });
    }
  };
  addGlobalEventListener(EventName.CLOSE_MQTT_CLIENT, closeCb);

  return () => {
    console.log('正在销毁 MQTT Channel...');
    removeGlobalEventListener(EventName.CREATE_MQTT_CLIENT, createCb);
    removeGlobalEventListener(EventName.CLOSE_MQTT_CLIENT, closeCb);
    if (innerMqttClient) {
      innerMqttClient.end();
      innerMqttClient = null;
      delete (window as any).mqttClient;
    }
  };
}, buffers.expanding<MqttEmitData>(32));

function* watchMqttResponse(): IterableIterator<any> {
  while (true) {
    try {
      const emitData: any = yield take(mqttChannel);
      switch (emitData.type) {
        case ConnectionStatus.MESSAGE:
          {
            const resp = JSON.parse(emitData.data) || {};
            const { header, data } = resp;

            if (emitData.topic.startsWith(TopicPath.CONTROL_PAD_WILL)) {
              const androidStatus = data?.online
                ? Status.ONLINE
                : Status.OFFLINE;
              yield put({
                type: SYNC_ANDRIOD_CONNECT_STATUS,
                payload: { header, data: { ...data, androidStatus } },
              });
            } else {
              const msgType = header.message_type || header.messageType;
              if (msgType === 'SCREEN_COMMAND_RESULT') {
                sendGlobalEvent('command_res_change', resp);
              }
              yield put({ type: msgType, payload: resp });
            }
          }
          break;

        case ConnectionStatus.CONNECTION_LOST:
          console.warn('MQTT 链接已断开');
          break;
      }
    } catch (error) {
      console.error('MQTT Saga 运行异常:', error);
    }
  }
}

export default function* rootSagas() {
  wsChannel = createWsEventChannal(process.env.JDX_APP_COCKPIT_WS!);
  yield all([watchMqttResponse(), watchWsResponse(), watchHTTPRequest()]);
}
